import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../api/client.js';
import { PostChart } from '../charts/PostChart.js';
import { PostDrawer } from '../ui/PostDrawer.js';
import { MoveAccountModal } from '../ui/MoveAccountModal.js';
import { AccountToggler } from '../ui/AccountToggler.js';
import { TagsBlock } from '../ui/TagsBlock.js';
import { TopPostsList } from '../ui/TopPostsList.js';
import { formatSubName } from '../lib/subName.js';
import type { ChartRange, ModelDetail, PostChartPoint, TwitterAccountSummary } from '@wml-crm/shared';

const RANGES: { key: ChartRange; label: string }[] = [
  { key: '1m', label: '1 мес' },
  { key: '3m', label: '3 мес' },
  { key: '6m', label: '6 мес' },
  { key: 'all', label: 'Всё' },
];

function firstLetter(s: string): string {
  const c = s.trim().charAt(0);
  return c ? c.toUpperCase() : '?';
}

export function ModelPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const [model, setModel] = useState<ModelDetail | null>(null);
  const [range, setRange] = useState<ChartRange>('3m');
  const [topRange, setTopRange] = useState<ChartRange>('3m');
  const [points, setPoints] = useState<PostChartPoint[]>([]);
  const [topPoints, setTopPoints] = useState<PostChartPoint[]>([]);
  const [selected, setSelected] = useState<PostChartPoint | null>(null);
  const [resetKey, setResetKey] = useState(0);
  const [moving, setMoving] = useState<TwitterAccountSummary | null>(null);
  const [hidden, setHidden] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!id) return;
    api<ModelDetail>(`/api/models/${id}`).then(setModel).catch(() => setModel(null));
  }, [id]);

  useEffect(() => {
    if (!id) return;
    api<PostChartPoint[]>(`/api/models/${id}/chart?range=${range}`)
      .then(setPoints)
      .catch(() => setPoints([]));
  }, [id, range]);

  useEffect(() => {
    if (!id) return;
    api<PostChartPoint[]>(`/api/models/${id}/chart?range=${topRange}`)
      .then(setTopPoints)
      .catch(() => setTopPoints([]));
  }, [id, topRange]);

  if (!model) return <div className="empty-state">Загрузка…</div>;

  // Статистики считаем ТОЛЬКО по видимым аккам, иначе цифры разъезжаются
  // со скрытыми линиями на графике.
  const visiblePoints = points.filter((p) => !hidden.has(p.accountId));
  const totalViews = visiblePoints.reduce((s, p) => s + p.views, 0);
  const bestPost = visiblePoints.reduce<PostChartPoint | null>((best, p) => !best || p.views > best.views ? p : best, null);

  return (
    <div className="model-page">
      <div className="model-header">
        <div className="head-left">
          <div className="head-avatar">{firstLetter(model.displayName)}</div>
          <div>
            <div className="head-name-row">
              <h1>{model.displayName}</h1>
              <button
                className="rename-btn"
                title="Переименовать модель (только внутри CRM)"
                onClick={async () => {
                  const suggested = model.displayName;
                  const input = window.prompt(
                    `Новое имя модели.\nАвто-имя (по слейвам): ${model.autoName}\nОставьте пустым — вернуть авто-имя.`,
                    model.displayName === model.autoName ? '' : suggested,
                  );
                  if (input === null) return;
                  const trimmed = input.trim();
                  try {
                    await api(`/api/models/${model.id}/name`, {
                      method: 'PATCH',
                      body: JSON.stringify({ name: trimmed || null }),
                    });
                    const fresh = await api<ModelDetail>(`/api/models/${model.id}`);
                    setModel(fresh);
                  } catch {
                    window.alert('Не удалось переименовать');
                  }
                }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 20h9" />
                  <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
                </svg>
                Переименовать
              </button>
            </div>
            {model.displayName !== model.autoName && (
              <div style={{ fontSize: 12, color: 'var(--text-subtle)', marginTop: 2 }}>
                авто-имя: {model.autoName}
              </div>
            )}
            <div className="accounts">
              {model.accounts.map((a) => (
                <span key={a.id} className="chip chip-with-action">
                  <span>{formatSubName(a.slaveName, model.autoName) || a.slaveName}</span>
                  <button
                    className="chip-action"
                    title="Перенести на другую модель"
                    onClick={() => setMoving(a)}
                  >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M7 16V4M7 4 3 8M7 4l4 4" />
                      <path d="M17 8v12M17 20l-4-4M17 20l4-4" />
                    </svg>
                  </button>
                </span>
              ))}
            </div>
          </div>
        </div>
        <TagsBlock
          modelId={model.id}
          tags={model.tags ?? []}
          onChange={(next) => setModel({ ...model, tags: next })}
        />
        <div className="range-tabs">
          {RANGES.map((r) => (
            <button
              key={r.key}
              className={range === r.key ? 'active' : ''}
              onClick={() => setRange(r.key)}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      <div className="stats-row">
        <div className="stat-card">
          <div className="stat-label">Постов в периоде</div>
          <div className="stat-value">{visiblePoints.length}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Суммарный охват</div>
          <div className="stat-value">{totalViews.toLocaleString('ru-RU')}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Лучший пост</div>
          <div className="stat-value stat-accent">{bestPost ? bestPost.views.toLocaleString('ru-RU') : '—'}</div>
        </div>
      </div>

      <div className="card card-large">
        {points.length === 0 ? (
          <div className="empty-state">За выбранный период нет данных.</div>
        ) : (
          <PostChart
            points={points}
            hiddenAccountIds={hidden}
            modelDisplayName={model.autoName}
            onPointClick={setSelected}
            resetKey={resetKey}
          />
        )}
      </div>

      {model.accounts.length > 1 && (
        <AccountToggler
          accounts={model.accounts}
          modelDisplayName={model.autoName}
          hidden={hidden}
          onToggle={(id) => {
            setHidden((s) => {
              const n = new Set(s);
              if (n.has(id)) n.delete(id); else n.add(id);
              return n;
            });
          }}
          onShowAll={() => setHidden(new Set())}
          onHideAll={() => setHidden(new Set(model.accounts.map((a) => a.id)))}
        />
      )}

      <TopPostsList
        points={topPoints}
        accounts={model.accounts}
        modelDisplayName={model.autoName}
        range={topRange}
        onRangeChange={setTopRange}
        onSelect={setSelected}
      />

      {selected && (
        <PostDrawer
          point={selected}
          onClose={() => { setSelected(null); setResetKey((k) => k + 1); }}
          onReuseChanged={(postId, count) => {
            setPoints((prev) => prev.map((p) => p.postId === postId ? { ...p, reuseCount: count } : p));
            setTopPoints((prev) => prev.map((p) => p.postId === postId ? { ...p, reuseCount: count } : p));
          }}
        />
      )}

      {moving && model && (
        <MoveAccountModal
          account={moving}
          currentModelId={model.id}
          currentModelName={model.displayName}
          onClose={() => setMoving(null)}
          onDone={(newModelId) => {
            setMoving(null);
            // Ушёл последний акк? — модели могло не остаться, редиректим на страницу новой.
            if (model.accounts.length === 1) nav(`/model/${newModelId}`);
            else api<ModelDetail>(`/api/models/${id}`).then(setModel).catch(() => {});
          }}
        />
      )}
    </div>
  );
}
